import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Autocomplete, Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import { fetchRepsData } from '../../redux/slices/repSlice';
import SearchField from '../../components/input/SearchField';
import Layout from '../../components/Layout/Layout';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import Edit from './Edit';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import KeyIcon from '@mui/icons-material/Key';
import EditPassword from './EditPassword';
import { productCategories } from '../../assets/data';

export default function Rep() {
    let { user: currentUser } = useSelector((state) => state?.user);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);

    const [searchQuery, setSearchQuery] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [user, setUser] = useState(null);

    const [filteredData, setFilteredData] = useState(null);

    const dispatch = useDispatch();
    const { data, isLoading, error } = useSelector((state) => state.rep);

    React.useEffect(() => {
        dispatch(fetchRepsData());
    }, []);

    React.useEffect(() => {
        setFilteredData(data);

        return () => {
            setFilteredData(null);
        };
    }, [data]);

    const handleSearch = (searchText, category = null) => {
        setSearchQuery(searchText);
    
        // If no search text and no category, show all data
        if (!searchText && !category) {
            setFilteredData(data);
            return;
        }
    
        // Filter data based on search text and optionally category if provided
        const filteredResults = data.filter((row) => {
            // Check if search text matches (if provided)
            const matchesSearchText = searchText
                ? row?.firstName?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.lastName?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.userName?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.email?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.phoneNo?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.address?.toLowerCase().includes(searchText?.toLowerCase()) ||
                  row?.area?.toLowerCase().includes(searchText?.toLowerCase())
                : true;
    
            // Check if category matches (if provided)
            const matchesCategory = category ? row?.viewProductsCategory?.includes(category) : true;
    
            // Both conditions must be true (AND logic)
            return matchesSearchText && matchesCategory;
        });
    
        setFilteredData(filteredResults);
    };

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `users/${user?.id}`, {});

            if (res?.data) {
                setUser(null);
                dispatch(fetchRepsData());
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    return (
        <Layout>
            <>
                <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                    <Grid item lg={5} sm={3.5} md={6.5} xs={6}>
                        <Typography variant="h6">Reps</Typography>
                    </Grid>

                    {currentUser?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Rep
                            </Button>
                        </Grid>
                    )}

                    <Grid item lg={2.5} sm={5} md={3} xs={12}>
                        <Autocomplete
                            size="small"
                            options={productCategories}
                            getOptionLabel={(cat) => cat}
                            value={selectedCategory}
                            onChange={(event, selected) => {
                                setSelectedCategory(selected);
                                handleSearch(searchQuery, selected);
                            }}
                            renderInput={(params) => <TextField {...params} label="Category" variant="outlined" />}
                        />
                    </Grid>

                    <Grid item lg={3} sm={5} md={3} xs={12}>
                        <SearchField
                            value={searchQuery}
                            onChange={(e) => handleSearch(e?.target?.value, selectedCategory)}
                            label={'Search Reps'}
                        />
                    </Grid>

                    {currentUser?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Rep
                            </Button>
                        </Grid>
                    )}
                </Grid>
                {isLoading && !data && <Loader />}

                {!!data && (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone No.</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Area</TableCell>
                                    {currentUser?.role === 'super-admin' && <TableCell sx={{ width: '60px' }} />}
                                    <TableCell sx={{ width: '60px' }} />
                                    {currentUser?.role === 'super-admin' && <TableCell sx={{ width: '60px' }} />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData?.map((row) => (
                                    <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                            {row.serialNo}
                                        </TableCell>
                                        <TableCell>
                                            {row.firstName} {row.lastName}
                                        </TableCell>
                                        <TableCell>{row.userName}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{row.phoneNo}</TableCell>
                                        <TableCell>{row.address}</TableCell>
                                        <TableCell>{row.area}</TableCell>
                                        {currentUser?.role === 'super-admin' && (
                                            <TableCell>
                                                <IconButton
                                                    onClick={() => {
                                                        setUser(row);
                                                        setShowEditModal(true);
                                                    }}
                                                    aria-label="Edit"
                                                    size="small"
                                                >
                                                    <BorderColorIcon sx={{ color: '#1976d2' }} />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setUser(row);
                                                    setShowPasswordModal(true);
                                                }}
                                                aria-label="Change Password"
                                                size="small"
                                            >
                                                <KeyIcon sx={{ color: '#1976d2' }} />
                                            </IconButton>
                                        </TableCell>
                                        {currentUser?.role === 'super-admin' && (
                                            <TableCell>
                                                <IconButton
                                                    onClick={() => {
                                                        setUser(row);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    aria-label="Delete"
                                                    size="small"
                                                >
                                                    <DeleteIcon sx={{ color: 'red' }} />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!!error && <ErrorScreen error={error} />}
            </>

            <ModalCustom open={showCreateModal} title={'Create Rep'}>
                <AddNew onClose={() => setShowCreateModal(false)} />
            </ModalCustom>

            <ModalCustom open={showEditModal} title={'Edit Rep'}>
                <Edit user={user} onClose={() => setShowEditModal(false)} />
            </ModalCustom>

            <ModalCustom open={showPasswordModal} title={'Edit Password'}>
                <EditPassword user={user} onClose={() => setShowPasswordModal(false)} />
            </ModalCustom>

            <ModalConfirmation
                open={showDeleteModal}
                loading={loadingDelReq}
                header={'Delete Rep'}
                description={`Are you sure, you want to delete ${user?.firstName} ${user?.lastName}?`}
                onClickNo={() => setShowDeleteModal(false)}
                onClickYes={handleDelete}
            />
        </Layout>
    );
}
